import { useAwaitQuery } from '@/common/hooks/useAwaitQuery';
import { useGoogleReCaptcha } from '@/common/hooks/useGoogleReCaptcha';
import { isUseProductionEnv } from '@/constants/is-use-production-env';
import { SESSION_SIGN_UP_SHARE_CODE_KEY, SIGN_UP_SHARE_CODE_URL_KEY } from '@/constants/shareCode';
import { useCartContext } from '@/context/cart';
import { useEventingContext } from '@/context/eventing';
import { useUserContext } from '@/context/user';
import { retrieveCartId } from '@/store/actions/cart/asyncActions';
import { fetchClientIp } from '@/util/fetch-client-ip';
import mergeOperations from '@/util/shallowMerge';
import { useApolloClient, useMutation } from '@apollo/client';
import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useRef, useState } from 'react';
import DEFAULT_OPERATIONS from './createAccount.gql';

export const useCreateAccount = props => {
  const { initialValues = {}, onSubmit, onCancel, onCreateAccountSuccess } = props;

  const operations = mergeOperations(DEFAULT_OPERATIONS, props.operations);
  const {
    createAccountMutation,
    createCartMutation,
    getCartDetailsQuery,
    getCustomerQuery,
    mergeCartsMutation,
    signInMutation,
  } = operations;
  const apolloClient = useApolloClient();
  const searchParams = useSearchParams();

  const formApiRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(true);

  const [isLoading, setIsLoading] = useState(false);

  const [isAgree, setIsAgree] = useState(true);
  const [{ cartId }, { createCart, removeCart, getCartDetails }] = useCartContext();
  const [{ isGettingDetails }, { getUserDetails, setToken }] = useUserContext();

  const [, { dispatch }] = useEventingContext();

  const [fetchCartId] = useMutation(createCartMutation);

  const [mergeCarts] = useMutation(mergeCartsMutation);

  // For create account and sign in mutations, we don't want to cache any
  // personally identifiable information (PII). So we set fetchPolicy to 'no-cache'.
  const [createAccount, { error: createAccountError }] = useMutation(createAccountMutation, {
    fetchPolicy: 'no-cache',
  });

  const [signIn, { error: signInError }] = useMutation(signInMutation, {
    fetchPolicy: 'no-cache',
  });

  const fetchUserDetails = useAwaitQuery(getCustomerQuery);
  const fetchCartDetails = useAwaitQuery(getCartDetailsQuery);

  const { generateReCaptchaData, recaptchaLoading, recaptchaWidgetProps } = useGoogleReCaptcha({
    currentForm: 'CUSTOMER_CREATE',
    formAction: 'createAccount',
  });

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleCancelKeyPress = useCallback(() => {
    event => {
      if (event.key === 'Enter') {
        handleCancel();
      }
    };
  }, [handleCancel]);

  const handleSubmit = useCallback(
    async formState => {
      const { values } = formState;

      const { customer, password, subscribe } = values;

      if (!isAgree) {
        return;
      }

      if (!isSubmitting) {
        return;
      }

      const variables = {
        email: customer.email,
        password: password,
        is_subscribed: !!isSubmitting,
      };

      let shareCode = searchParams.get(SIGN_UP_SHARE_CODE_URL_KEY);

      if (!shareCode) {
        shareCode = sessionStorage.getItem(SESSION_SIGN_UP_SHARE_CODE_KEY);
      }

      if (shareCode) {
        variables.share_code = shareCode;
      }

      try {
        setIsLoading(true);
        // Get source cart id (guest cart id).
        const sourceCartId = cartId;

        // Get reCaptchaV3 Data for createAccount mutation
        const recaptchaDataForCreateAccount = await generateReCaptchaData();

        // Create the account and then sign in.
        await createAccount({
          variables,
          ...recaptchaDataForCreateAccount,
        });

        sessionStorage.removeItem(SESSION_SIGN_UP_SHARE_CODE_KEY);

        dispatch({
          type: 'USER_CREATE_ACCOUNT',
          payload: {
            email: customer.email,
            isSubscribed: !!subscribe,
          },
        });

        // Get reCaptchaV3 Data for signIn mutation
        const recaptchaDataForSignIn = await generateReCaptchaData();

        const signInResponse = await signIn({
          variables: {
            email: customer.email,
            password: password,
          },
          ...recaptchaDataForSignIn,
        });
        const token = signInResponse.data.generateCustomerToken.token;

        await setToken(token);

        // Clear all cart/customer data from cache and redux.
        await apolloClient.clearCacheData(apolloClient, 'cart');
        await apolloClient.clearCacheData(apolloClient, 'customer');
        await removeCart();

        // Create and get the customer's cart id.
        await createCart({
          fetchCartId,
        });
        const destinationCartId = await retrieveCartId();

        // Merge the guest cart into the customer cart.

        if (sourceCartId) {
          await mergeCarts({
            variables: {
              destinationCartId,
              sourceCartId,
            },
          });
        }

        // Ensure old stores are updated with any new data.
        await getUserDetails({
          fetchUserDetails,
        });
        await getCartDetails({
          fetchCartId,
          fetchCartDetails,
        });

        try {
          if (isUseProductionEnv) {
            fetchClientIp().then(currentIp => {
              fetch('/api/pinterest-signup', {
                method: 'POST',
                body: JSON.stringify({
                  email: customer.email,
                  ip: currentIp,
                }),
              });
            });
          }
        } catch (e) {
          console.log('Error', e);
        }

        // Finally, invoke the post-submission callback.
        if (onSubmit) {
          onSubmit();
        }

        if (onCreateAccountSuccess) {
          onCreateAccountSuccess();
        }

        setIsLoading(false);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error(error);
        }

        setIsLoading(false);
      }
    },

    [
      isAgree,
      isSubmitting,
      searchParams,
      cartId,
      generateReCaptchaData,
      createAccount,
      dispatch,
      signIn,
      setToken,
      apolloClient,
      removeCart,
      createCart,
      fetchCartId,
      mergeCarts,
      getUserDetails,
      fetchUserDetails,
      getCartDetails,
      fetchCartDetails,
      onSubmit,
      onCreateAccountSuccess,
    ],
  );

  const sanitizedInitialValues = useMemo(() => {
    const { email, firstName, lastName, ...rest } = initialValues;

    return {
      customer: {
        email,
        firstname: firstName,
        lastname: lastName,
      },
      ...rest,
    };
  }, [initialValues]);

  const errors = useMemo(
    () =>
      new Map([
        ['createAccountQuery', createAccountError],
        ['signInMutation', signInError],
      ]),
    [createAccountError, signInError],
  );

  return {
    errors,
    handleCancel,
    handleSubmit,
    handleCancelKeyPress,
    initialValues: sanitizedInitialValues,
    isDisabled: isGettingDetails || recaptchaLoading,
    recaptchaWidgetProps,
    isSubmitting,
    setIsSubmitting,
    isAgree,
    setIsAgree,
    isLoading,
    formApiRef,
  };
};
